import { useEffect, useMemo, useState } from 'react';
import {
  billingAccountExtensionNameMsg,
  billingAccountMissingMsg,
  billingAccountNameMsg,
  contactInfoMsg,
  deliveryMethodMsg,
  eInvoicingAddressMsg,
  eInvoicingOperatorMsg,
  emailInvoiceMsg,
  installationAddressMissingMsg,
  invoiceLanguageMsg,
  loadingProductInformationMsg,
  payerAddressMsg,
  payerBusinessIdMsg,
  payerDetailsMsg,
  payerNameMsg,
  productSelectionMissingMsg,
  referenceMsg,
  t,
} from '../../../common/i18n/index.js';
import { parseCurrencyToNumber } from '../../../common/utils/priceUtils.js';
import { OFFieldApi } from '../../../generated/api/oFFieldApi.js';
import { OFPageType } from '../../../generated/api/oFPageType.js';
import { OFQuestionDataType } from '../../../generated/api/oFQuestionDataType.js';
import { OFQuestionType } from '../../../generated/api/oFQuestionType.js';
import { any, has, not } from '../OpenForm.js';
import { type OpenFormAnswers, type OpenFormContext } from '../OpenFormAnswers.js';
import { useOpenFormAsync } from '../OpenFormProvider.js';
import {
  getAddressText,
  getAvailabilityAddressDetails,
  getAvailabilityAddressText,
  getDate,
  getValue,
  isAvailabilityAddress,
} from '../OpenFormUtils.js';
import { zeroDetailsQuestion, zeroReasonSelect } from '../OpenFormView/OpenFormSummary/OpenFormZeroPrice.js';
import { type OpenForm } from './useOpenForm.js';
import type { OpenFormCustomerNeedAdditionalInformation } from '../../../generated/api/openFormCustomerNeedAdditionalInformation.js';
import type { OpenFormCustomerNeedRequest } from '../../../generated/api/openFormCustomerNeedRequest.js';
import type { OpenFormProductSummary } from '../../../generated/api/openFormProductSummary.js';
import type { OpenFormQuestion } from '../../../generated/api/openFormQuestion.js';
import type { OpenFormSummaryResponse } from '../../../generated/api/openFormSummaryResponse.js';
import type { ParameterizedSearchContactResult } from '../../../generated/api/parameterizedSearchContactResult.js';

export class OpenFormSummary {
  readonly products: (OpenFormProductSummary & { choice: string })[];
  readonly services: (OpenFormProductSummary & { choice: string })[];
  readonly #answers: OpenFormAnswers;
  readonly #form: OpenForm;
  readonly #order: OpenFormQuestion[];

  constructor({ products }: OpenFormSummaryResponse, form: OpenForm, answers: OpenFormAnswers) {
    this.#answers = answers;
    this.#form = form;
    this.#order = form.sections.filter(has(OFPageType.ORDER_ENRICHMENT)).flatMap(s => s.questions);
    this.products = products.flatMap(this.#openFormProductSummary(OFPageType.QUESTION_MAIN_PRODUCTS));
    this.services = products.flatMap(this.#openFormProductSummary(OFPageType.QUESTION_ADDITIONAL_SERVICES));
  }

  get zeroPriceMismatch() {
    return this.oneTimeZeroPriceMismatch || this.recurringZeroPriceMismatch;
  }

  get oneTimeZeroPriceMismatch() {
    return (
      this.products.some(this.#hasOneTimeZeroPriceMismatch) || this.services.some(this.#hasOneTimeZeroPriceMismatch)
    );
  }

  get recurringZeroPriceMismatch() {
    return (
      this.products.some(this.#hasRecurringZeroPriceMismatch) || this.services.some(this.#hasRecurringZeroPriceMismatch)
    );
  }

  get zeroPriceValid(): boolean {
    const reason = this.#answers.get(zeroDetailsQuestion.guid)?.choices?.[0] ?? '';
    const details = this.#answers.get(zeroReasonSelect.guid)?.choices?.[0] ?? '';

    if (this.recurringZeroPriceMismatch) {
      return !!reason.trim() && !!details.trim();
    }
    if (this.oneTimeZeroPriceMismatch) {
      return !!reason.trim();
    }
    return true;
  }

  get billingAccountNew() {
    return this.#answers.findOf('billingAccountNew', (_, guid) =>
      this.#order.some(has(OFFieldApi.Billing_Account__c, guid))
    )[1];
  }

  get billingAccountOld() {
    return this.#answers.findOf('billingAccountOld', (_, guid) =>
      this.#order.some(has(OFFieldApi.Billing_Account__c, guid))
    )[1];
  }

  get contactEntries() {
    return this.#answers
      .entriesOf('contact')
      .map(([guid, contact]) => [this.#order.find(has(guid)), contact])
      .filter((entry): entry is [OpenFormQuestion, ParameterizedSearchContactResult] => Boolean(entry[0]));
  }

  get deliveryDate() {
    return getValue(
      this.#answers.findOf('choices', (_, guid) => this.#order.some(has(OFQuestionType.DATE_OF_DELIVERY, guid)))[1]
    );
  }

  get deliveryTime() {
    return getValue(
      this.#answers.findOf('choices', (_, guid) => this.#order.some(has(OFQuestionType.TIME_OF_DELIVERY, guid)))[1]
    );
  }

  get installationAddress() {
    return this.#answers.findOf('address', ({ address }) => isAvailabilityAddress(address))[1];
  }

  get subscriptionReferencesEntries() {
    return Object.entries(
      this.#answers.findOf('subscriptionReferences', (_, guid) =>
        this.#order.some(has(OFQuestionType.SUBSCRIPTION_REFERENCE_ID, guid))
      )[1] ?? {}
    );
  }

  readonly #mapContactAnswers = (context: OpenFormContext, question: OpenFormQuestion) => {
    const contact = context.contact;
    return !context.choices
      ? []
      : {
          guid: question.guid,
          answers: context.choices.map(choiceId => ({
            value: [contact?.FirstName, contact?.LastName].filter(Boolean).join(' ') || choiceId,
          })),
        };
  };

  get openFormCustomerNeedRequest(): OpenFormCustomerNeedRequest {
    const main = this.products.find(p => p.productCode);
    if (!main) {
      throw t.RZXL(productSelectionMissingMsg);
    }

    return {
      formId: this.#form.formId,
      questions: this.#form.questions.flatMap(this.#openFormCustomerNeedQuestion),
      referenceNumber: this.#form.referenceNumber,
      rootProducts: [
        {
          product: this.#openFormCustomerNeedProduct(main),
          childProducts: this.services.map(this.#openFormCustomerNeedProduct),
          additionalInformation: this.#openFormCustomerNeedAdditionalInformation,
        },
      ],
    };
  }

  get #openFormCustomerNeedAdditionalInformation() {
    return this.#form.questions
      .flatMap<OpenFormCustomerNeedAdditionalInformation>(q => {
        switch (q.fieldApiName) {
          case '':
          case undefined:
          case OFFieldApi.Billing_Account__c:
          case OFFieldApi.Installation_Address__c:
          case OFFieldApi.Product_line__c:
            return [];
          case OFFieldApi.Service_availability_customer_expenses__c:
            return {
              fieldLabel: q.fieldApiName,
              fieldValue: parseCurrencyToNumber(getValue(this.#answers.get(q)?.choices))?.toString(),
            };
        }
        switch (q.type) {
          case OFQuestionType.SUBSCRIPTION_REFERENCE_ID:
            return [];
          case OFQuestionType.CONTRACT_PERIOD:
            return {
              fieldLabel: q.fieldApiName,
              fieldValue: getValue(this.#form.linkedValues(q, this.#answers.get(q)?.choices)),
            };
          default:
            return {
              fieldLabel: q.fieldApiName,
              fieldValue: getValue(this.#answers.get(q)?.choices),
            };
        }
      })
      .concat(this.#openFormCustomerNeedAdditionalInformationBillingAccount)
      .concat(this.#openFormCustomerNeedAdditionalInformationInstallationAddress)
      .concat(this.#openFormCustomerNeedAdditionalInformationProductLine)
      .concat(this.#openFormCustomerNeedAdditionalInformationSubscriptionReferences)
      .concat(
        this.zeroPriceMismatch
          ? [
              {
                fieldLabel: OFFieldApi.Zero_Balance_Reason__c,
                fieldValue: getValue(this.#answers.get(zeroReasonSelect.guid)?.choices),
              },
              {
                fieldLabel: OFFieldApi.Zero_Balance_Details__c,
                fieldValue: getValue(this.#answers.get(zeroDetailsQuestion.guid)?.choices),
              },
            ]
          : []
      );
  }

  get #openFormCustomerNeedAdditionalInformationBillingAccount() {
    const account = this.billingAccountOld;
    if (account) {
      return {
        fieldLabel: OFFieldApi.Billing_Account__c,
        fieldValue: account.billingAccountId,
      };
    }
    const data = this.billingAccountNew;
    if (data) {
      return {
        fieldLabel: OFFieldApi.Billing_Account_Data__c,
        fieldValue: JSON.stringify(
          Object.keys(data).flatMap(key => {
            switch (key) {
              case 'billElectronicAddress':
                return { key: key, value: data[key], displayValue: data[key], label: eInvoicingAddressMsg };
              case 'billElectronicOperator':
                return {
                  key: key,
                  value: data[key],
                  displayValue: data.billElectronicOperatorLabel,
                  label: eInvoicingOperatorMsg,
                };
              case 'billLanguage':
                return { key: key, value: data[key], displayValue: data[key], label: invoiceLanguageMsg };
              case 'billReceiverEmail':
                return { key: key, value: data[key], displayValue: data[key], label: emailInvoiceMsg };
              case 'billingAccountExtensionName':
                return {
                  key: key,
                  value: data[key],
                  displayValue: data.billingAccountExtensionName,
                  label: billingAccountExtensionNameMsg,
                };
              case 'billingAccountName':
                return { key: key, value: data[key], displayValue: data[key], label: billingAccountNameMsg };
              case 'billingContactId':
                return { key: key, value: data[key], displayValue: data.billingContactName, label: contactInfoMsg };
              case 'customerReference1':
                return { key: key, value: data[key], displayValue: data[key], label: `${referenceMsg} 1` };
              case 'customerReference2':
                return { key: key, value: data[key], displayValue: data[key], label: `${referenceMsg} 2` };
              case 'deliveryMethod':
                return { key: key, value: data[key], displayValue: data[key], label: deliveryMethodMsg };
              case 'payerAddress':
                return {
                  key: key,
                  value: JSON.stringify(data[key]),
                  displayValue: getAddressText(data[key]),
                  label: payerAddressMsg,
                };
              case 'payerBusinessId':
                return { key: key, value: data[key], displayValue: data[key], label: payerBusinessIdMsg };
              case 'payerName':
                return { key: key, value: data[key], displayValue: data[key], label: payerNameMsg };
              case 'payerNameExtension':
                return { key: key, value: data[key], displayValue: data[key], label: payerDetailsMsg };
              default:
                return [];
            }
          })
        ),
      };
    }
    throw t.TZS0(billingAccountMissingMsg);
  }

  get #openFormCustomerNeedAdditionalInformationInstallationAddress() {
    const installationAddress = this.installationAddress;
    if (installationAddress) {
      return [
        {
          fieldLabel: OFFieldApi.Installation_Address__c,
          fieldValue: getAvailabilityAddressText(installationAddress),
        },
        {
          fieldLabel: OFFieldApi.Additional_Installation_Address_Details__c,
          fieldValue: getAvailabilityAddressDetails(installationAddress),
        },
      ];
    } else if (this.#form.sections.every(not(OFPageType.AVAILABILITY_CHECK))) {
      return [];
    }
    throw t.DRXL(installationAddressMissingMsg);
  }

  get #openFormCustomerNeedAdditionalInformationProductLine() {
    return {
      fieldLabel: OFFieldApi.Product_line__c,
      fieldValue: this.#answers.valuesOf('subscriptionTypes').flat().join(','),
    };
  }

  get #openFormCustomerNeedAdditionalInformationSubscriptionReferences() {
    return this.subscriptionReferencesEntries
      .filter(([_, value]) => value)
      .map(([fieldLabel, fieldValue]) => ({ fieldLabel, fieldValue }));
  }

  readonly #openFormCustomerNeedProduct = ({ choice, ...props }: OpenFormProductSummary & { choice: string }) => {
    const charge = this.#answers.findOf('charges', ({ choices }) => choices?.includes(choice))[1]?.[choice];
    return {
      productCode: props.productCode,
      oneOffCharge: String(parseCurrencyToNumber(charge?.[OFFieldApi.one_time_charge__c]) ?? props.oneOffCharge),
      recurringCharge: String(parseCurrencyToNumber(charge?.[OFFieldApi.recurring_charge__c]) ?? props.recurringCharge),
    };
  };

  readonly #openFormCustomerNeedQuestion = (question: OpenFormQuestion) => {
    const context = this.#answers.isVisible(question) && this.#answers.get(question);
    if (!context) {
      return [];
    }

    const CONTACT_TYPES = [
      OFQuestionType.DELIVERY_CONTACT,
      OFQuestionType.FAULT_INCIDENT_CONTACT,
      OFQuestionType.INSTALLATION_CONTACT,
      OFQuestionType.ORDERING_CONTACT,
      OFQuestionType.TECHNICAL_CONTACT,
    ] as const;

    if ((CONTACT_TYPES as readonly OFQuestionType[]).includes(question.type)) {
      return this.#mapContactAnswers(context, question);
    }

    switch (question.type) {
      case OFQuestionType.ADDITIONAL_INFORMATION_BACKOFFICE:
      case OFQuestionType.ADDITIONAL_INFORMATION_FIELD_ENGINEER:
      case OFQuestionType.BA_SELECTION:
      case OFQuestionType.INSTALLATION_ADDRESS:
      case OFQuestionType.INSTALLATION_CONTACT:
      case OFQuestionType.TIME_OF_DELIVERY:
      case OFQuestionType.CONTRACT_PERIOD:
      case OFQuestionType.LIST_OF_OBJECTS_MULTI_SELECT:
      case OFQuestionType.LIST_OF_OBJECTS_SINGLE_SELECT:
      case OFQuestionType.MULTI_SELECT:
      case OFQuestionType.SINGLE_SELECT:
        return !context.choices
          ? []
          : { guid: question.guid, answers: context.choices.map(choice => ({ guid: choice })) };
      case OFQuestionType.DATE_OF_DELIVERY:
        return !context.choices
          ? []
          : { guid: question.guid, answers: context.choices.map(choice => ({ value: getDate(choice) })) };
      case OFQuestionType.SUBSCRIPTION_REFERENCE_ID: {
        const references = Object.values(context.subscriptionReferences ?? {}).filter(Boolean);
        return !references.length
          ? []
          : { guid: question.guid, answers: references.map(reference => ({ value: reference })) };
      }
      case OFQuestionType.FREE_TEXT:
        switch (question.dataType) {
          case OFQuestionDataType.EMAIL:
          case OFQuestionDataType.PHONE:
          case OFQuestionDataType.TEXT:
          case OFQuestionDataType.TIME_FIELD:
            return !context.choices
              ? []
              : { guid: question.guid, answers: context.choices.map(choice => ({ value: choice })) };
          case OFQuestionDataType.DATE_FIELD:
            return !context.choices
              ? []
              : { guid: question.guid, answers: context.choices.map(choice => ({ value: getDate(choice) })) };
          case OFQuestionDataType.NUMBER_CURRENCY:
            return !context.choices
              ? []
              : {
                  guid: question.guid,
                  answers: context.choices.map(choice => ({ value: parseCurrencyToNumber(choice)?.toString() })),
                };
          case OFQuestionDataType.NUMBER_INTEGER:
            return !context.choices
              ? []
              : { guid: question.guid, answers: context.choices.map(choice => ({ value: Number(choice).toFixed() })) };
          default:
            return [];
        }
      default:
        return [];
    }
  };

  readonly #openFormProductSummary = (pageType: OFPageType) => {
    const choices = this.#form.sections.filter(has(pageType)).flatMap(s => s.questions.flatMap(q => q.choices));
    return ({ choice, ...p }: OpenFormProductSummary) => (choice && choices.some(has(choice)) ? { choice, ...p } : []);
  };

  readonly #hasRecurringZeroPriceMismatch = (p: OpenFormProductSummary & { choice: string }): boolean => {
    const chargeCtx = this.#answers.findOf('charges', c => c.charges?.[p.choice])[1];
    if (!chargeCtx) {
      return false;
    }

    const charge = chargeCtx[p.choice] ?? {};

    return charge.recurring_charge__c === '0.00' && p.recurringCharge !== 0;
  };

  readonly #hasOneTimeZeroPriceMismatch = (p: OpenFormProductSummary & { choice: string }): boolean => {
    const chargeCtx = this.#answers.findOf('charges', c => c.charges?.[p.choice])[1];
    if (!chargeCtx) {
      return false;
    }

    const charge = chargeCtx[p.choice] ?? {};

    return charge.one_time_charge__c === '0.00' && p.oneOffCharge !== 0;
  };
}

export const useOpenFormSummary = (form: OpenForm, answers: OpenFormAnswers) => {
  const [data, setData] = useState<OpenFormSummary>();
  const async = useOpenFormAsync();
  const choices = useMemo(
    () =>
      form.sections
        .filter(any(OFPageType.QUESTION_MAIN_PRODUCTS, OFPageType.QUESTION_ADDITIONAL_SERVICES))
        .flatMap(s => s.questions.filter(not(OFQuestionType.FREE_TEXT)).flatMap(q => answers.get(q)?.choices ?? []))
        .join(','),
    [form, answers]
  );

  useEffect(() => {
    const { controller } = async.fetch
      .getOpenFormSummary({ choices: choices.split(',') })
      .resolved(response => setData(new OpenFormSummary(response, form, answers)))
      .rejected(text => async.actions.notification({ text, type: 'warning' }))
      .cache(choices)
      .execute({ disable: true, message: t.VBS1(loadingProductInformationMsg) });

    return () => controller.abort();
  }, [form, answers, async, choices]);

  return data;
};
